/**
 * Ce que l'export des données emporte — et ce qu'il laisse, avec la raison.
 *
 * Toute table du schéma `public` est classée ici, d'un côté ou de l'autre.
 * `supabase/tests/export-classification.test.ts` échoue dès qu'une table
 * existe sans être classée : c'est ce qui empêche la prochaine table créée de
 * sortir de l'export sans que personne le voie. Classer une table « exclue »
 * est une décision — elle se justifie ici, en une phrase, pas dans un commit.
 *
 * Pur : aucune lecture. L'export lui-même lit ces listes ; il ne les décide pas.
 */

/** Dossiers de l'archive. Un patron s'y retrouve sans connaître le schéma. */
export type ExportFolder =
  | "entreprise"
  | "commercial"
  | "chantiers"
  | "equipe"
  | "achats"
  | "factur-x";

export interface ExportedTable {
  folder: ExportFolder;
  /** Colonnes retirées de l'export, et pourquoi. */
  omit?: Readonly<Record<string, string>>;
  /**
   * Colonnes JSON qui recopient des lignes d'autres tables : on y retire les
   * mêmes clés que l'on retire des colonnes. Sans quoi un secret écarté de
   * `tenants` repartirait par la copie qu'en garde le journal d'audit.
   */
  scrubJson?: readonly string[];
  /**
   * Colonnes qui ordonnent la lecture, `id` par défaut. La base rend les
   * lignes par pages ; sans un ordre total, une ligne peut glisser d'une page
   * à l'autre et sortir de l'archive — ou y entrer deux fois.
   */
  key?: readonly string[];
}

/** La clé de lecture d'une table exportée. */
export function exportKey(spec: ExportedTable): readonly string[] {
  return spec.key ?? ["id"];
}

/**
 * Les secrets de la fiche entreprise. Ce sont des clés d'accès à des
 * services tiers, pas des données du client — et elles doivent de toute
 * façon être révoquées à son départ.
 *
 * Les identifiants (`piste_client_id`, `piste_technical_user`) partent avec :
 * ce ne sont que la moitié d'une paire, inutiles et trompeurs sans l'autre.
 */
export const TENANT_SECRET_COLUMNS: Readonly<Record<string, string>> = {
  einvoice_pa_credentials: "Clé d'accès SUPER PDP.",
  piste_client_id: "Identifiant PISTE, moitié d'une paire dont le secret est retiré.",
  piste_client_secret: "Secret PISTE (Chorus Pro).",
  piste_technical_user: "Compte technique PISTE, moitié d'une paire dont le mot de passe est retiré.",
  piste_technical_password: "Mot de passe du compte technique PISTE.",
};

export const EXPORTED_TABLES: Readonly<Record<string, ExportedTable>> = {
  // ─── L'entreprise ──────────────────────────────────────────────────────
  // Seule table exportée sans `tenant_id` : sa ligne est l'entreprise même.
  tenants: { folder: "entreprise", omit: TENANT_SECRET_COLUMNS },
  tenant_config: { folder: "entreprise", key: ["tenant_id"] },
  users: { folder: "entreprise" },
  expense_categories: { folder: "entreprise" },
  reminder_sequences: { folder: "entreprise" },
  cashflow_forecasts: { folder: "entreprise" },
  audit_logs: { folder: "entreprise", scrubJson: ["old_data", "new_data"] },

  // ─── Le commercial ─────────────────────────────────────────────────────
  clients: { folder: "commercial" },
  client_contacts: {
    folder: "commercial",
    omit: { token_hash: "Empreinte du lien d'accès au portail : une clé, pas une donnée." },
  },
  catalog_items: { folder: "commercial" },
  quotes: { folder: "commercial" },
  quote_lines: { folder: "commercial" },
  invoices: { folder: "commercial" },
  payments: { folder: "commercial" },
  reminder_actions: { folder: "commercial" },
  document_sends: { folder: "commercial" },

  // ─── Les chantiers ─────────────────────────────────────────────────────
  projects: { folder: "chantiers" },
  project_phases: { folder: "chantiers" },
  // Les lots par corps de métier, et les phases qui en attendent d'autres.
  project_lots: { folder: "chantiers" },
  project_phase_links: { folder: "chantiers" },
  // Ce qui est confié à un sous-traitant — lot, phase ou tâche —, et pour combien.
  project_subcontracts: { folder: "chantiers" },
  project_assignments: { folder: "chantiers" },
  tasks: { folder: "chantiers" },
  appointments: { folder: "chantiers" },
  field_reports: { folder: "chantiers" },
  // Le registre des photos ; les fichiers partent dans leur propre archive.
  project_photos: { folder: "chantiers" },
  // Plans et cahiers des charges ; les fichiers partent dans
  // `chantiers/documents/<chantier>/`.
  project_documents: { folder: "chantiers" },
  // Le matériel : le parc, ce qui est sur chaque chantier, et l'historique de
  // chaque mouvement — ce qu'on relit quand une machine a disparu.
  equipment: { folder: "chantiers" },
  equipment_bulk_items: { folder: "chantiers" },
  equipment_bulk_placements: { folder: "chantiers" },
  equipment_movements: { folder: "chantiers" },

  // ─── L'équipe ──────────────────────────────────────────────────────────
  employees: { folder: "equipe" },
  employee_costs: { folder: "equipe", key: ["employee_id"] },
  time_entries: { folder: "equipe" },
  site_attendance: { folder: "equipe" },
  employee_absences: { folder: "equipe" },
  employee_certifications: { folder: "equipe" },
  safety_talks: { folder: "equipe" },
  safety_talk_attendees: { folder: "equipe", key: ["talk_id", "employee_id"] },
  admin_deadlines: { folder: "equipe" },

  // ─── Les achats ────────────────────────────────────────────────────────
  suppliers: { folder: "achats" },
  subcontractors: { folder: "achats" },
  // Leurs pièces obligatoires ; les fichiers partent avec le bucket `documents`.
  subcontractor_documents: { folder: "achats" },
  // Leurs factures, et ce qu'on leur a réglé — retenue rendue comprise.
  subcontract_invoices: { folder: "achats" },
  subcontract_payments: { folder: "achats" },
  // Les liens de planning donnés aux sous-traitants : à qui, quand, combien de fois ouverts.
  subcontract_planning_links: {
    folder: "achats",
    omit: { token_hash: "Empreinte du lien de planning : une clé, pas une donnée." },
  },
  expenses: { folder: "achats" },
  received_invoices: { folder: "achats" },
  material_orders: { folder: "achats" },
  stock_movements: { folder: "achats" },

  // ─── La facturation électronique ───────────────────────────────────────
  einvoice_transmissions: { folder: "factur-x" },
};

export const EXCLUDED_TABLES: Readonly<Record<string, string>> = {
  // Ce qui appartient à DKER
  ai_model_pricing: "Tarifs des modèles d'IA : notre grille, commune à tous, sans donnée client.",
  ai_usage: "Consommation d'IA : notre coût, pas sa donnée.",
  demo_sandboxes: "Registre des bacs à sable de la démonstration.",
  staff_users: "Comptes de l'éditeur.",
  staff_impersonations: "Sessions de support de l'éditeur, déjà visibles du patron dans l'application.",
  tenant_signup_grants: "Autorisations de mise en service, posées par l'éditeur.",
  tenant_terminations: "Résiliation posée par l'éditeur ; ses dates s'affichent dans l'application.",
  tenant_closures: "Fiches de clôture tenues par l'éditeur après suppression, sans donnée client.",

  // Des clés, pas des données
  piste_tokens: "Jetons d'accès PISTE en cours de validité.",
  user_invitations: "Codes d'invitation à usage unique, consommés ou périmés.",

  // La plomberie
  ai_alerts: "Alertes calculées par l'application, recalculées à chaque passage.",
  einvoice_directory: "Annuaire public des e-factures, commun à tous, sans donnée client.",
  idempotency_keys: "Garde contre les doubles envois, expire en quelques heures.",
  pending_actions: "Confirmations en attente : un état transitoire de l'application.",
  photo_retention_notices: "Trace des avertissements de purge des photos, propre à DKER.",
  tenant_domains: "Domaines servis par DKER.",
};

/**
 * Motif des colonnes qui ressemblent à un secret. Une table exportée ne peut
 * en porter une que si elle est retirée par `omit` — le test le vérifie, pour
 * qu'une colonne `stripe_api_key` ajoutée demain ne parte pas dans un `.zip`.
 */
export const SECRET_COLUMN_PATTERN = /secret|password|credential|token|api_?key/i;

/**
 * Les buckets du Storage. `documents` part dans l'archive des données,
 * `photos` dans la sienne (le volume se compte en centaines de Mo), et
 * `branding` — le logo — avec la fiche entreprise ; la photo de chaque machine
 * dans `materiel/photos/`. `landing-media` porte le
 * film de la landing : aucune donnée d'entreprise, il n'est pas exporté.
 */
export const EXPORTED_BUCKETS: Readonly<Record<string, string>> = {
  documents: "documents/",
  photos: "Archive séparée, par le bouton qui existe déjà.",
  branding: "entreprise/",
  "chantier-documents": "chantiers/documents/<chantier>/",
  "equipment-photos": "materiel/photos/",
  "landing-media": "Non exporté : médias publics de la landing, communs à tous, sans donnée client.",
};
